import { useCallback, useEffect, useRef, useState } from 'react';
import { CardRow } from '../components/CardRow';
import { MediaUploader } from '../components/MediaUploader';
import { PostPreview } from '../components/PostPreview';
import { RichTextEditor, type RichTextEditorHandle } from '../components/RichTextEditor';
import { Button, ErrorBanner, InfoBanner, Screen, Spinner } from '../components/Ui';
import { ApiError, api, type CardPayload } from '../lib/api';
import { clearDraft, formatSavedAt, loadDraft, moveDraft, saveDraft } from '../lib/draftStorage';
import { notify } from '../lib/telegram';
import {
  DEFAULT_LIMITS,
  type CardDraft,
  type MediaLayout,
  type PostShape,
  type Me,
  type PostLimits,
  type PostMediaItem,
} from '../types';

type Props = {
  me: Me;
  collectionId?: string;
  onDone: () => void;
  onPreview: (id: string) => void;
};

let keyCounter = 0;
const nextKey = () => `draft-${++keyCounter}`;

export function EditorView({ me, collectionId, onDone, onPreview }: Props) {
  const [links, setLinks] = useState('');
  const [caption, setCaption] = useState('');
  // Свой текст кнопки для этого поста; пусто — сервер подставит значение
  // по умолчанию.
  const [ownButtonText, setOwnButtonText] = useState('');
  const [cards, setCards] = useState<CardDraft[]>([]);
  const [media, setMedia] = useState<PostMediaItem[]>([]);
  // У новой подборки лимиты берутся из /api/me: зашитые в код значения
  // остаются только запасным вариантом на случай старого ответа сервера.
  const [limits, setLimits] = useState<PostLimits>(me.limits ?? DEFAULT_LIMITS);
  const [savedId, setSavedId] = useState<string | undefined>(collectionId);

  const [parsing, setParsing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(Boolean(collectionId));
  const [error, setError] = useState<string | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [published, setPublished] = useState<string | null>(null);

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  // Текст кнопки задаётся на сервере — фронтенд его не дублирует, иначе
  // превью начнёт расходиться с тем, что реально уходит в канал.
  const [buttonText, setButtonText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [postShape, setPostShape] = useState<PostShape | null>(null);

  // Канал публикации выбирается здесь: сначала тестовый, потом основной.
  const [channelId, setChannelId] = useState<number | undefined>(
    () => me.channels.find((c) => c.isDefault)?.id ?? me.channels[0]?.id,
  );

  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [restored, setRestored] = useState(false);
  const [alreadyPublished, setAlreadyPublished] = useState(false);
  const [mediaLayout, setMediaLayout] = useState<MediaLayout>('collage');
  const editorRef = useRef<RichTextEditorHandle>(null);

  /**
   * Автор что-то менял. Пока нет — не сохраняем ничего ни локально, ни на
   * сервер: открытие поста на чтение не должно его переписывать. Это защита
   * от целого класса потерь, а не только от одной уже исправленной ошибки.
   */
  const [dirty, setDirty] = useState(false);
  const touch = useCallback(() => setDirty(true), []);

  // Режим редактирования уже существующей подборки.
  useEffect(() => {
    if (!collectionId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getCollection(collectionId);
        if (cancelled) return;
        setMedia(res.collection.media);
        setLimits(res.limits);
        setAlreadyPublished(res.collection.status === 'published');
        setMediaLayout(res.collection.mediaLayout ?? 'collage');

        // Локальный черновик новее серверного, если приложение выгрузили
        // до того, как автосохранение успело уйти. Тогда берём его.
        const local = loadDraft(collectionId);
        const serverSavedAt = new Date(res.collection.updatedAt ?? 0).getTime();
        // Локальную копию берём, если она новее серверной ИЛИ если на сервере
        // текста нет, а локально есть: автосохранение могло не успеть уйти,
        // и терять написанное из-за гонки часов недопустимо.
        const localHasMore = Boolean(local?.caption.trim()) && !res.collection.caption.trim();
        if (local && (local.savedAt > serverSavedAt || localHasMore)) {
          setCaption(local.caption);
          setCards(local.cards);
          setOwnButtonText(local.buttonText ?? '');
          setRestored(true);
          return;
        }

        setOwnButtonText(res.collection.buttonText ?? '');
        setCaption(res.collection.caption);
        setCards(
          res.collection.cards.map((card) => ({
            key: nextKey(),
            url: card.url,
            title: card.title,
            imageUrl: card.imageUrl,
            imageProxyUrl: card.imageProxyUrl,
            brand: card.brand,
            price: card.price,
            currency: card.currency,
            retailer: card.retailer,
            missing: [],
          })),
        );
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Не удалось открыть подборку');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [collectionId]);

  // Восстановление черновика новой подборки после выгрузки приложения.
  useEffect(() => {
    if (collectionId) return;
    const local = loadDraft(undefined);
    if (!local) return;
    setCaption(local.caption);
    setCards(local.cards);
    setOwnButtonText(local.buttonText ?? '');
    setRestored(true);
  }, [collectionId]);

  // Локальное автосохранение — синхронное, без сети, на каждое изменение.
  // Именно оно спасает данные, когда вебвью закрывают на полуслове.
  useEffect(() => {
    if (loading) return;
    if (!dirty) return;
    saveDraft(savedId ?? collectionId, { caption, buttonText: ownButtonText, cards });
  }, [caption, ownButtonText, cards, savedId, collectionId, loading, dirty]);

  const toPayload = useCallback(
    (): CardPayload[] =>
      cards.map((card) => ({
        url: card.url,
        title: card.title.trim() || card.retailer || 'Товар',
        imageUrl: card.imageUrl,
        brand: card.brand,
        price: card.price,
        currency: card.currency,
        retailer: card.retailer,
        parsedBy: card.parsedBy,
      })),
    [cards],
  );

  const save = useCallback(async (): Promise<string> => {
    const payload = {
      caption,
      buttonText: ownButtonText || null,
      mediaLayout,
      cards: toPayload(),
    };
    if (savedId) {
      await api.updateCollection(savedId, payload);
      setSavedAt(Date.now());
      return savedId;
    }
    const res = await api.createCollection(payload);
    setSavedId(res.collection.id);
    // Черновик «new» переезжает под настоящий id, иначе всплывёт при
    // следующем создании подборки.
    moveDraft(res.collection.id);
    return res.collection.id;
  }, [caption, ownButtonText, mediaLayout, savedId, toPayload]);

  /**
   * Медиа привязываются к подборке, поэтому перед первой загрузкой файла
   * черновик нужно сохранить. Для блогера это незаметно.
   */
  const ensureCollectionId = useCallback(async (): Promise<string> => {
    if (savedId) return savedId;
    return save();
  }, [savedId, save]);

  // Превью собирает сервер — той же функцией, что и настоящую публикацию,
  // поэтому оно не расходится с результатом. Дёргаем с задержкой, чтобы не
  // ходить на каждый символ.
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      setPreviewLoading(true);
      void api
        .postPreview({ caption, mediaCount: media.length })
        .then((res) => {
          setPreviewHtml(res.preview.messageHtml);
          setButtonText(res.preview.defaultButtonText);
          setPostShape(res.preview.shape);
        })
        .catch(() => setPreviewHtml(null))
        .finally(() => setPreviewLoading(false));
    }, 400);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [caption, media.length]);

  const targetChannel = me.channels.find((c) => c.id === channelId) ?? null;

  /**
   * Серверное автосохранение поверх локального: реже, с задержкой, и только
   * когда есть что сохранять. Локальная копия уже защищает от выгрузки, здесь
   * задача — чтобы черновик пережил смену устройства.
   */
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Свежая ссылка на save для обработчиков событий: они вешаются один раз,
  // а save меняется на каждое изменение текста.
  const saveRef = useRef(save);
  saveRef.current = save;

  const hasContent = Boolean(caption.trim()) || cards.length > 0;
  const canAutosave = !loading && !published && hasContent && dirty;

  useEffect(() => {
    if (!canAutosave) return;

    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      void save()
        .then(() => setSavedAt(Date.now()))
        .catch(() => {
          // Молча: локальная копия на месте, а лезть с ошибкой в лицо на
          // каждом автосохранении — худшее, что можно сделать.
        });
    }, 1200);

    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [caption, ownButtonText, cards, canAutosave, save]);

  /**
   * Сворачивание приложения — момент, когда черновик теряется чаще всего:
   * вебвью Telegram могут выгрузить сразу после этого, не дождавшись
   * отложенного автосохранения. Поэтому здесь сохраняем немедленно.
   */
  useEffect(() => {
    if (!canAutosave) return;

    const flush = () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      void saveRef
        .current()
        .then(() => setSavedAt(Date.now()))
        .catch(() => {});
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, [canAutosave]);

  const freeSlots = limits.maxCards - cards.length;

  const parseLinks = async () => {
    if (!links.trim()) return;
    setParsing(true);
    setError(null);
    setParseErrors([]);
    try {
      const res = await api.parse({ text: links });
      const added: CardDraft[] = [];
      const failed: string[] = [];

      for (const result of res.results) {
        if (!result.ok) {
          failed.push(`${shortUrl(result.url)} — ${result.error}`);
          continue;
        }
        if (cards.some((c) => c.url === result.card.url)) continue;
        if (added.length >= freeSlots) {
          failed.push(`${shortUrl(result.url)} — превышен лимит ${limits.maxCards} товаров`);
          continue;
        }
        added.push({ key: nextKey(), ...result.card, missing: result.missing });
      }

      if (added.length > 0) touch();
      setCards((prev) => [...prev, ...added]);
      setParseErrors(failed);
      if (added.length > 0) {
        setLinks('');
        notify('success');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось разобрать ссылки');
      notify('error');
    } finally {
      setParsing(false);
    }
  };

  const move = (index: number, direction: -1 | 1) => {
    touch();
    setCards((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const copy = [...prev];
      const [item] = copy.splice(index, 1);
      copy.splice(target, 0, item!);
      return copy;
    });
  };

  const remove = (index: number) => {
    touch();
    setCards((prev) => prev.filter((_, i) => i !== index));
  };

  const change = (index: number, patch: Partial<CardDraft>) => {
    touch();
    return setCards((prev) =>
      prev.map((card, i) => {
        if (i !== index) return card;
        const next = { ...card, ...patch };
        // Подсказка «не распозналось» должна гаснуть, как только поле заполнено.
        return { ...next, missing: missingFieldsOf(next) };
      }),
    );
  };

  const publish = async () => {
    setPublishing(true);
    setError(null);
    try {
      const id = await save();
      const res = await api.publish(id, channelId);
      setPublished(res.published.deepLink);
      clearDraft(id);
      notify('success');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось опубликовать');
      notify('error');
    } finally {
      setPublishing(false);
    }
  };

  const saveAndExit = async () => {
    setPublishing(true);
    setError(null);
    try {
      await save();
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <Screen>
        <Spinner />
      </Screen>
    );
  }

  if (published) {
    return (
      <Screen>
        <div className="success">
          <div className="success__icon">✅</div>
          <h2>Опубликовано</h2>
          <p className="muted">
            Пост с кнопкой{buttonText ? ` «${buttonText}»` : ''} уже в канале
            {targetChannel?.title ? ` «${targetChannel.title}»` : ''}.
          </p>
          <div className="deeplink">{published}</div>
          <Button full onClick={() => savedId && onPreview(savedId)} variant="secondary">
            Посмотреть, что увидит подписчик
          </Button>
          <Button full onClick={onDone} variant="ghost">
            К списку подборок
          </Button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <header className="editor-header">
        <h1>{savedId ? 'Редактирование' : 'Новый пост'}</h1>
      </header>

      {me.channels.length === 0 && (
        <InfoBanner>
          Канал не подключён — опубликовать не получится. Добавь бота админом в канал на
          главном экране.
        </InfoBanner>
      )}

      {alreadyPublished && (
        <InfoBanner>
          Пост уже опубликован. Правки можно вносить, но публикация создаст в канале
          <b> новый пост</b> — старый останется на месте, удали его вручную, если он
          не нужен.
        </InfoBanner>
      )}

      {restored && (
        <InfoBanner>
          Восстановлен несохранённый черновик — приложение закрылось раньше, чем данные
          ушли на сервер.
        </InfoBanner>
      )}

      <section className="block">
        <div className="label">Фото и видео</div>
        {media.length > 1 && (
          <div className="layout-switch">
            <button
              className={`layout-switch__item ${mediaLayout === 'collage' ? 'layout-switch__item--active' : ''}`}
              onClick={() => {
                touch();
                setMediaLayout('collage');
              }}
            >
              Сеткой
            </button>
            <button
              className={`layout-switch__item ${mediaLayout === 'slideshow' ? 'layout-switch__item--active' : ''}`}
              onClick={() => {
                touch();
                setMediaLayout('slideshow');
              }}
            >
              Слайдером
            </button>
          </div>
        )}
        {media.length > 1 && mediaLayout === 'collage' && media.some((m) => m.kind === 'video') && (
          <p className="muted small">
            Сетку раскладывает сам Telegram, и вертикальное видео он растягивает на всю
            колонку — рядом остаётся пустое место. Со слайдером такого не бывает.
          </p>
        )}

        <MediaUploader
          media={media}
          onChange={(next) => {
            touch();
            setMedia(next);
          }}
          onInsertIntoText={(item, position) => {
            touch();
            editorRef.current?.insertMedia(
              item.id,
              `${item.kind === 'video' ? 'видео' : 'фото'} ${position + 1}`,
            );
          }}
          ensureCollectionId={ensureCollectionId}
          limits={limits}
        />
      </section>

      <section className="block">
        <div className="label">Текст поста</div>
        <RichTextEditor
          ref={editorRef}
          value={caption}
          onChange={(html) => {
            touch();
            setCaption(html);
          }}
          maxLength={limits.maxTextLength}
          placeholder="Расскажи о подборке. Можно выделять текст, ставить ссылки и цитаты."
        />
      </section>

      <section className="block">
        <label className="label" htmlFor="links">
          Товары подборки
          <span className="muted">
            {' '}
            · осталось {Math.max(0, freeSlots)} из {limits.maxCards}
          </span>
        </label>
        <p className="muted small">
          В пост они не попадают: названия, цены и фото товаров видны только в
          мини-аппе, куда ведёт кнопка.
        </p>
        <textarea
          id="links"
          className="textarea"
          value={links}
          onChange={(e) => setLinks(e.target.value)}
          placeholder={'Вставь ссылки — можно списком или прямо текстом\nhttps://shop.com/item-1\nhttps://shop.com/item-2'}
          rows={3}
          disabled={freeSlots <= 0}
        />
        <Button
          full
          onClick={parseLinks}
          loading={parsing}
          disabled={!links.trim() || freeSlots <= 0}
          variant="secondary"
        >
          {parsing ? 'Читаю магазины…' : 'Добавить товары'}
        </Button>
        {parseErrors.length > 0 && (
          <div className="parse-errors">
            {parseErrors.map((message) => (
              <div key={message} className="parse-errors__item">
                {message}
              </div>
            ))}
          </div>
        )}
      </section>

      {cards.length > 0 && (
        <section className="block">
          <div className="label">Товары в подборке</div>
          <div className="card-rows">
            {cards.map((card, index) => (
              <CardRow
                key={card.key}
                card={card}
                index={index}
                total={cards.length}
                onMove={move}
                onRemove={remove}
                onChange={change}
              />
            ))}
          </div>
        </section>
      )}

      <PostPreview
        messageHtml={previewHtml}
        media={media}
        buttonText={ownButtonText}
        onButtonTextChange={(value) => {
          touch();
          setOwnButtonText(value);
        }}
        buttonPlaceholder={buttonText}
        channelTitle={targetChannel?.title}
        loading={previewLoading}
        shape={postShape}
      />
      <p className="muted small">
        Текст кнопки можно изменить прямо в превью. Оставишь пустым — будет
        «{buttonText}».{' '}
        {(ownButtonText || buttonText).length > 20 && (
          <span className="warn">
            Подпись длиннее 20 знаков ({(ownButtonText || buttonText).length}) —
            Telegram растянет пост под кнопку, и по краям появится пустота.
            Короче подпись — уже пост.
          </span>
        )}
      </p>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {me.channels.length > 1 && (
        <section className="block">
          <div className="label">Куда публиковать</div>
          <div className="channel-picker">
            {me.channels.map((channel) => (
              <button
                key={channel.id}
                className={`channel-picker__item ${
                  channel.id === channelId ? 'channel-picker__item--active' : ''
                }`}
                onClick={() => setChannelId(channel.id)}
              >
                <span className="channel-picker__title">{channel.title ?? channel.chatId}</span>
                {channel.username && (
                  <span className="channel-picker__username">@{channel.username}</span>
                )}
              </button>
            ))}
          </div>
          <p className="muted small">
            Опубликуй сначала в тестовый канал, проверь пост и мини-аппу, затем ту же
            подборку — в основной. Пересоздавать её не нужно.
          </p>
        </section>
      )}

      <div className="actions">
        {savedAt !== null && (
          <p className="muted small center">Черновик сохранён · {formatSavedAt(savedAt)}</p>
        )}
        <Button
          full
          onClick={publish}
          loading={publishing}
          disabled={cards.length === 0 || !channelId}
        >
          {alreadyPublished ? 'Опубликовать заново' : 'Опубликовать в канал'}
        </Button>
        {cards.length === 0 && (
          <p className="muted small center">
            Добавь хотя бы один товар — без него кнопка под постом ведёт в пустоту.
          </p>
        )}
        <Button full variant="ghost" onClick={saveAndExit} disabled={publishing}>
          Сохранить черновик
        </Button>
        <button className="link-btn" onClick={onDone}>
          Отмена
        </button>
      </div>
    </Screen>
  );
}

function missingFieldsOf(card: CardDraft): string[] {
  const missing: string[] = [];
  if (!card.title.trim()) missing.push('title');
  if (!card.imageUrl) missing.push('imageUrl');
  if (!card.brand?.trim()) missing.push('brand');
  return missing;
}

function shortUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.slice(0, 40);
  }
}
